import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import type { Filters, StpInstance } from '@ipfabric/utils';
import { instanceMembersApiEndpoint } from 'app/components/STPInstanceMembers/stpInstanceEndpoints';
import { ApiTable } from 'components/ApiTable/components/ApiTable';
import { Button } from 'components/Button/Button';
import { deviceExplorerTableCellContext } from 'components/DeviceView/DeviceViewContent/DeviceViewTable/deviceExplorerTableCellContext';
import { IsolateInstancesToggle } from 'components/IsolateInstancesToggle/IsolateInstancesToggle';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { useLocale } from 'containers/App/LocaleContext';
import { type FC, memo } from 'react';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';

export type StpInstanceUpdateEvent = {
  instance: StpInstance;
};

export type StpInstanceIsolateEvent = {
  isolate: boolean;
};

interface RowData {
  id: string;
  rootHostname: string;
  // STP ID (its MAC address) of the Root Bridge for this virtual port
  rootId: string;
  // VLAN ID in which the STP Instance on this Virtual port is located
  vlanId: number;
  // VLAN NAME in which the STP Instance on this Virtual port is located
  vlanName: string;
}

export interface STPInstanceMembersProps {
  isolateStpInstances?: boolean;
  onStpInstanceIsolate?: (event: StpInstanceIsolateEvent) => void;
  onStpInstanceUpdate?: (event: StpInstanceUpdateEvent) => void;
  snapshotId: string | null;
  staticFilters?: Filters;
  stpInstances?: StpInstance[];
  tableId: string;
}

const isRowSelected = (rowData: RowData, stpInstances?: StpInstance[]) =>
  stpInstances?.some(
    (instance) =>
      instance.rootId === rowData.rootId &&
      instance.vlanId === rowData.vlanId &&
      instance.visible,
  ) ?? false;

const BareSTPInstanceMembers: FC<STPInstanceMembersProps> = (props) => {
  const { technologySpanTreeInstanceMembers: instanceMembersMsg } = useLocale();

  const handleAddStp = (rootId: string, vlanId: number) => () => {
    props.onStpInstanceUpdate?.({
      instance: { rootId, vlanId, visible: true },
    });
  };

  const handleRemoveStp = (rootId: string, vlanId: number) => () => {
    props.onStpInstanceUpdate?.({
      instance: { rootId, vlanId, visible: false },
    });
  };

  const handleIsolateStp = (checked: boolean) => {
    props.onStpInstanceIsolate?.({
      isolate: checked,
    });
  };

  const endpoint = useTableEndpoint({
    apiEndpoint: instanceMembersApiEndpoint,
    alternative: {
      id: props.tableId,
      columns: [
        '...',
        createFrontendOnlyColumn({
          key: 'action',
          disabledSort: true,
          title: instanceMembersMsg.actionColumnTitle,
          webDependencies: ['rootId', 'vlanId'],
          render: (rowData: RowData) => {
            const stpInstanceIsVisible = isRowSelected(
              rowData,
              props.stpInstances,
            );

            return (
              <>
                <Tooltip content={instanceMembersMsg.actions.show}>
                  <Button
                    disabled={stpInstanceIsVisible}
                    icon='fa-circle-plus'
                    onClick={handleAddStp(rowData.rootId, rowData.vlanId)}
                    type='button'
                    size='xxs'
                    variant='tertiary'
                  />
                </Tooltip>
                <Tooltip content={instanceMembersMsg.actions.hide}>
                  <Button
                    disabled={!stpInstanceIsVisible}
                    icon='fa-trash-can'
                    onClick={handleRemoveStp(rowData.rootId, rowData.vlanId)}
                    type='button'
                    size='xxs'
                    variant='tertiary'
                  />
                </Tooltip>
              </>
            );
          },
        }),
      ],
    },
  });

  return (
    <>
      {props.stpInstances !== undefined && (
        <div className='px py-1 border-b border-default'>
          <IsolateInstancesToggle
            checked={!!props.isolateStpInstances}
            disabled={props.stpInstances.every((instance) => !instance.visible)}
            id='stpInstanceMembersIsolateToggle'
            labelText={instanceMembersMsg.isolateInstances}
            labelTooltipText={instanceMembersMsg.isolateInstancesTooltip}
            onChange={handleIsolateStp}
          />
        </div>
      )}
      <ApiTable
        borderless
        className='flex-1'
        staticFilters={props.staticFilters}
        endpoint={endpoint}
        snapshotId={props.snapshotId}
        tableCellContext={deviceExplorerTableCellContext}
      />
    </>
  );
};

export const STPInstanceMembers = memo(BareSTPInstanceMembers);
